/* eslint-disable no-redeclare */
/* (we're using overloads) */
import { useTellescopeInfiniteQuery, type UseTellescopeInfiniteQueryOptions } from "..";
import type { TellescopeModelFromName } from "../../mappings";

const EMAIL_FETCH_LIMIT: number = 1000;

type EmailList = TellescopeModelFromName<"emails">[];

// Helper to normalize subject line by removing Re: prefixes
const normalizeSubject = (subject: string): string => {
  return subject.replace(/^(Re:\s*)+/i, "").trim();
};

// Filter and sort emails by subject line
const filterEmailsBySubject = (targetSubject: string, emails: TellescopeModelFromName<"emails">[]): EmailList => {
  const normalizedTarget = normalizeSubject(targetSubject);

  // Filter emails with matching normalized subject
  const matchingEmails = emails.filter((email) => {
    if (!email.subject) return false;
    return normalizeSubject(email.subject) === normalizedTarget;
  });

  // Sort chronologically by createdAt (earliest first)
  return matchingEmails.sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return dateA - dateB;
  });
};

export interface UseEmailThreadQueryArgs {
  enduserId: string;
  subjectLine: string;
}

// Base options type (without select)
type BaseEmailThreadQueryOptions<TError = Error> = Omit<
  UseTellescopeInfiniteQueryOptions<"emails", "getSome", string | undefined, TError, EmailList>,
  "initialPageParam" | "getNextPageParam" | "paginateRequest" | "select" | "model" | "method" | "request"
>;

// Options WITH select provided
export interface UseEmailThreadQueryOptionsWithSelect<TError = Error, TData = EmailList>
  extends BaseEmailThreadQueryOptions<TError> {
  select: (data: EmailList) => TData;
}

// Options WITHOUT select
export interface UseEmailThreadQueryOptionsWithoutSelect<TError = Error> extends BaseEmailThreadQueryOptions<TError> {
  select?: undefined;
}

// Overload 1: select provided → TData inferred from select return type
export function useEmailThreadQuery<TError = Error, TData = EmailList>(
  args: UseEmailThreadQueryArgs,
  options: UseEmailThreadQueryOptionsWithSelect<TError, TData>
): ReturnType<typeof useTellescopeInfiniteQuery<"emails", "getSome", string | undefined, TError, TData>>;

// Overload 2: no select → data is EmailList
export function useEmailThreadQuery<TError = Error>(
  args: UseEmailThreadQueryArgs,
  options?: UseEmailThreadQueryOptionsWithoutSelect<TError>
): ReturnType<typeof useTellescopeInfiniteQuery<"emails", "getSome", string | undefined, TError, EmailList>>;

// Implementation
export function useEmailThreadQuery<TError = Error, TData = EmailList>(
  { enduserId, subjectLine }: UseEmailThreadQueryArgs,
  options?: UseEmailThreadQueryOptionsWithSelect<TError, TData> | UseEmailThreadQueryOptionsWithoutSelect<TError>
) {
  const { select, enabled = true, ...restOptions } = options ?? {};

  return useTellescopeInfiniteQuery({
    model: "emails",
    method: "getSome",
    request: {
      filter: {
        enduserId,
      },
      limit: EMAIL_FETCH_LIMIT,
    },
    select: (data) => {
      const allEmails = data.pages.flat();
      const list = filterEmailsBySubject(subjectLine, allEmails);

      // When select is provided, use it; otherwise return list directly
      // The overloads guarantee type safety at the call site
      return select ? select(list) : list;
    },
    enabled: enabled && !!enduserId && !!subjectLine,
    ...restOptions,
  });
}
